import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { formatPerComparableUnit, formatSize, normalizedText } from '@domain/product'
import type { Product } from '@domain/product'
import type { Category } from '@domain/finance'
import { formatBRL } from '@shared/money/format'
import { Botao } from '@ui/glass/Botao'
import { Glass } from '@ui/glass/Glass'
import { Miniatura } from '@ui/produtos/Miniatura'
import { FolhaDeProduto } from '@ui/produtos/FolhaDeProduto'
import { Familias } from '@ui/produtos/Familias'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * O catálogo. `Docs/04-Design/Telas/Produtos.md` e a Fase 7.
 *
 * ## O número que importa é o PREÇO POR UNIDADE BASE
 *
 * A lista mostra o último preço pago **e** o preço por unidade base, e é o
 * segundo que responde a pergunta que justifica o projeto: *"500ml a R$ 3,99 é
 * corretamente apontado como mais caro que 1L a R$ 6,99"*. Ele vem em
 * milicentavos com cinco casas, porque é onde a diferença aparece — em duas
 * casas os dois viram "R$ 0,01". Ver `unit.ts`.
 *
 * ## A busca usa `normalizedText`, a mesma do matching
 *
 * Digitar "ype" acha "Ypê"; digitar "500ml" acha "500 ml". Uma busca por
 * `includes` sobre o nome cru falharia nas duas, e são exatamente as duas formas
 * em que alguém digita.
 *
 * ## O que NÃO está aqui, e o motivo
 *
 * **Leitura de código de barras pela câmera** e **upload de imagem** são as duas
 * entregas da fase que dependem de API de aparelho e de uma Function de
 * redimensionamento. Elas ficam para a próxima leva, e o campo de código de
 * barras aceita digitação por enquanto — um scanner que só funcionasse em metade
 * dos navegadores, sem o cadastro manual pronto, seria a parte bonita antes da
 * parte que funciona.
 */

function precoPorUnidade(product: Product): string | null {
  const { minPerBaseUnitMilliCents, baseUnit } = product.stats
  if (minPerBaseUnitMilliCents === null || baseUnit === null) return null
  return formatPerComparableUnit(minPerBaseUnitMilliCents, baseUnit)
}

/**
 * Uma embalagem na lista.
 *
 * **Registrar preço não mora aqui** (decisão 225). O atalho existia em toda
 * linha e cobrava por isso: o rótulo com o nome do produto quebrava em duas
 * linhas e dominava o bloco, e encurtá-lo deixava um verbo solto competindo
 * com o preço, que é o que esta tela existe para mostrar. Registrar é gesto de
 * quem já escolheu o produto — e vive na página dele, em botão de largura
 * cheia.
 *
 * ## A categoria mora na última linha do texto, e troca sem abrir o produto
 *
 * Arrumar a categoria de dezenas de produtos era abrir um, tocar em editar,
 * trocar, salvar e voltar — cinco gestos por produto. Aqui é o gatilho e a
 * escolha. É o mesmo `SeletorDeCategoria` do cadastro, e não o `<select>`
 * nativo que a decisão 250b deixava nas listas: o painel fecha ao escolher e a
 * lista continua montada embaixo dele, na mesma altura, então a objeção de
 * "tirar de vista a lista que se está varrendo" não se sustenta (decisão 286).
 */
function Linha({
  product,
  categorias,
  onCategoria,
  onCriouCategoria,
}: {
  product: Product
  categorias: Category[]
  onCategoria: (categoryId: string | null) => void
  onCriouCategoria: () => void
}) {
  const porUnidade = precoPorUnidade(product)
  const tamanho = formatSize(product.size, product.unit, product.packQuantity)
  const categoria = categorias.find((c) => c.id === product.categoryId)?.name ?? 'sem categoria'

  return (
    // `relative`: é a caixa que o link estica para cobrir. Ver o `<Link>`.
    <li className="relative flex items-stretch gap-3 border-b border-[var(--border)] py-3 text-sm">
      {/* A foto reserva o lugar MESMO sem imagem, ao contrário do desenho
          anterior.
          Antes o `<img>` simplesmente não era renderizado, com o argumento de
          que um quadrado vazio por linha era pior que nenhum — e naquela lista
          apertada era mesmo. Aqui a foto é estrutural: sem o espaço, cada nome
          começaria numa coluna diferente e a lista ficaria serrilhada. A
          inicial dentro da caixa tracejada diz "não há foto" sem parecer
          quebrado. */}
      <Miniatura nome={product.name} src={product.imageUrl} className="size-[72px]" />

      {/* **Tudo cabe na altura da foto**, e é o pedido do dono: o nome
          alinhado com o topo dela, marca e tamanho numa linha só, e a
          categoria embaixo. Com a categoria numa linha própria abaixo da foto,
          cada produto ocupava quase o dobro da altura. */}
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        {/* **O link é o nome, e se estica sobre a linha inteira** (`after:`
            cobre o `<li>`), então tocar na foto ou no preço continua levando
            ao produto. Ele não pode mais ENVOLVER a linha: a categoria mora no
            meio dela, e um botão dentro de um link é interativo aninhado — o
            toque no seletor navegaria junto. O seletor fica por cima do
            esticado (`relative z-10`).

            O sublinhado fica só no nome: é ele que nomeia o destino, e
            sublinhar o preço faria parecer que o número leva a outro lugar. */}
        <Link
          to="/produtos/$id"
          params={{ id: product.id }}
          data-nav-linha
          className="block truncate leading-5 font-medium underline after:absolute after:inset-0"
        >
          {product.isMonitored && <span aria-label="monitorado">★ </span>}
          {product.name}
        </Link>

        {/* Marca e tamanho na mesma linha, a marca mais apagada: ela DESEMPATA
            duas embalagens de nome parecido, não identifica o produto. */}
        <span className="truncate text-xs leading-4 text-[var(--text-muted)]">
          {product.brand !== null && product.brand !== '' && (
            <>
              <span className="text-[var(--text-faint)]">{product.brand}</span>
              {' · '}
            </>
          )}
          {tamanho ?? 'sem tamanho'}
        </span>

        <div className="relative z-10 max-w-full self-start">
          <SeletorDeCategoria
            compacto
            categorias={categorias}
            value={product.categoryId}
            rotulo={`Categoria de ${product.name}: ${categoria}`}
            className="max-w-full"
            onChange={onCategoria}
            onCriou={onCriouCategoria}
          />
        </div>
      </div>

      <div className="flex shrink-0 flex-col text-right">
        {/* O preço na face de display, como os números do Dashboard: esta tela
            existe para comparar dinheiro, e o olho cai aqui antes do nome. No
            topo, na linha do nome, e não centrado: centrado ele flutuava entre
            o nome e a categoria. */}
        {product.stats.lastPriceCents === null ? (
          <span className="text-xs text-[var(--text-muted)]">sem preço ainda</span>
        ) : (
          <span className="display text-[22px] leading-6">{formatBRL(product.stats.lastPriceCents)}</span>
        )}
        {/* O número que permite comparar. Sem tamanho declarado ele não existe,
            e a linha diz por quê em vez de mostrar um traço. */}
        <span
          className={`mt-1 text-xs tabular-nums ${
            porUnidade === null ? 'text-[var(--text-faint)]' : 'text-[var(--economia)]'
          }`}
        >
          {porUnidade ?? 'não compara'}
        </span>
      </div>
    </li>
  )
}

export function Produtos() {
  const { productRepository, categoryRepository } = useFinanceRepositories()
  const [lista, setLista] = useState<Product[] | 'loading' | 'failed'>('loading')
  const [categorias, setCategorias] = useState<Category[]>([])
  const [busca, setBusca] = useState('')
  const [soMonitorados, setSoMonitorados] = useState(false)
  /**
   * Embalagens ou grupos — e **na URL**, não em estado do componente.
   *
   * **Grupos é o padrão** desde a decisão 202: ninguém pensa "preciso de
   * Coca-Cola 2L", pensa "preciso de Coca". Qual embalagem comprar é a
   * resposta que o app dá, não a pergunta que ele faz. A lista de embalagens
   * continua a um toque, porque é onde se cadastra e se edita.
   *
   * **Na URL** porque em estado do componente ela não sobrevivia ao voltar:
   * quem abria uma embalagem e tocava em voltar caía em "O que a casa compra"
   * mesmo tendo saído de "Embalagens". Agora o endereço carrega a aba, e
   * recarregar e compartilhar o link também passam a funcionar.
   */
  // `strict: false` como em `Transacoes`: as rotas são montadas sob um id de
  // pai (`/protegidas/produtos`), e amarrar o componente a esse caminho o
  // acoplaria à forma da árvore em vez do endereço que a pessoa vê.
  const abaNaUrl = (useSearch({ strict: false }) as { aba?: string }).aba
  const navegar = useNavigate()
  const aba: 'produtos' | 'grupos' = abaNaUrl === 'embalagens' ? 'produtos' : 'grupos'

  /**
   * Trocar de aba **substitui** a entrada do histórico, e não empilha.
   *
   * Empilhando, voltar de um produto passaria por cada toque de aba antes de
   * sair da tela — e ninguém pensa em "desfazer uma troca de aba". O que se
   * quer preservado é a aba em que a pessoa estava quando saiu, e `replace`
   * guarda exatamente isso.
   */
  const trocarDeAba = (qual: 'produtos' | 'grupos') => {
    void navegar({
      to: '/produtos',
      search: qual === 'produtos' ? { aba: 'embalagens' } : {},
      replace: true,
    })
  }
  const [cadastrando, setCadastrando] = useState(false)
  // A aba de grupos tem leitura própria; esta versão avisa que um produto foi
  // gravado e ela precisa reler. Ver `Familias`.
  const [versaoDosGrupos, setVersaoDosGrupos] = useState(0)

  const recarregar = () => {
    void Promise.all([productRepository.list(), categoryRepository.list()])
      .then(([produtos, cats]) => {
        setLista(produtos)
        setCategorias(cats)
      })
      .catch((error: unknown) => {
        console.error('Produtos: a leitura falhou', error)
        setLista('failed')
      })
  }

  /**
   * A troca aparece na hora, e a gravação vai atrás.
   *
   * Esperar o Firestore para redesenhar deixaria o gatilho mostrando a
   * categoria velha por um instante depois da escolha — e quem está arrumando
   * vinte produtos seguidos toca de novo achando que não pegou. Se a gravação
   * falhar, a lista é relida e a categoria volta a ser a verdadeira.
   */
  const mudarCategoria = (id: string, categoryId: string | null) => {
    setLista((atual) =>
      Array.isArray(atual) ? atual.map((p) => (p.id === id ? { ...p, categoryId } : p)) : atual,
    )
    productRepository.update(id, { categoryId }).catch((error: unknown) => {
      console.error('Produtos: a categoria não foi gravada', error)
      recarregar()
    })
  }

  useEffect(() => {
    let live = true
    // `listMerchants` saiu daqui junto com o `RegistrarPreco` desta tela: era
    // uma leitura por abertura de catálogo para alimentar um formulário que
    // não existe mais aqui. A página do produto lê as lojas quando precisa.
    void Promise.all([productRepository.list(), categoryRepository.list()])
      .then(([produtos, cats]) => {
        if (!live) return
        setLista(produtos)
        setCategorias(cats)
      })
      .catch((error: unknown) => {
        console.error('Produtos: a leitura falhou', error)
        if (live) setLista('failed')
      })
    return () => {
      live = false
    }
  }, [productRepository, categoryRepository])

  // A busca passa pela MESMA normalização do catálogo: "ype" acha "Ypê", e
  // "500ml" acha "500 ml".
  const termo = normalizedText(busca)
  const visiveis = Array.isArray(lista)
    ? lista
        .filter((p) => p.archivedAt === null && (termo === '' || p.normalizedName.includes(termo)))
        .filter((p) => !soMonitorados || p.isMonitored)
        // Monitorado primeiro: é o que a pessoa marcou para acompanhar, e a
        // seção de Produtos existe para mostrar justamente esses.
        .sort((a, b) => Number(b.isMonitored) - Number(a.isMonitored))
    : []

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="titulo text-2xl">Produtos</h1>
        {/* Era um link sublinhado ao lado do título — lia como "voltar" ou
            "ver mais", e não como a ação que cria coisa.

            **`Glass` em vez de borda pintada à mão.** O material do app já
            resolve contorno, rim specular e sombra; refazer isso com `border`
            daria um botão parecido com vidro em vez de um botão DE vidro, e
            uma segunda verdade sobre como uma superfície se desenha aqui. O
            raio vira pílula, e o rim segue porque o `::before` herda o
            `border-radius`.

            **Tint e raio vão por `style`, e não por classe.** `glass.css` é
            importado sem `@layer`, então ele vence as utilities do Tailwind:
            `.glass` já define `background` e `border-radius`, e um `bg-*` ou
            `rounded-full` ao lado é escrito e ignorado. Medido aqui: com
            `rounded-full` na classe, o raio computado era 20 px — o
            `--radius-lg` do vidro. É o mesmo defeito da decisão 223, e ele
            vale para QUALQUER propriedade que o `.glass` já declare.

            O ícone gira 45° quando aberto, virando um "×": o mesmo controle
            abre e fecha, e a rotação diz isso sem trocar o rótulo — trocar o
            texto para "Cancelar" faria o alvo mudar de nome debaixo do dedo. */}
        <Botao
          desfoca
          tom={cadastrando ? 'primario' : 'secundario'}
          type="button"
          data-atalho-novo
          aria-expanded={cadastrando}
          onClick={() => {
            setCadastrando(!cadastrando)
          }}
        >
          <span
            aria-hidden="true"
            className={`inline-block text-base leading-none transition-transform ${
              cadastrando ? 'rotate-45' : ''
            }`}
          >
            +
          </span>
          Novo produto
        </Botao>
      </header>

      {/* Duas visões do mesmo catálogo, e a distinção é real: a lista de
          produtos é a de **embalagens** ("Coca 2L", "Coca lata"); a de grupos é
          a das **coisas que se compra**, e é onde se vê qual tamanho compensa
          sem abrir nada. */}
      <nav aria-label="Visão do catálogo" className="mb-4 flex gap-2">
        {(['grupos', 'produtos'] as const).map((qual) => (
          /* A pílula pintada é MENOR que o alvo, de propósito.
             O botão mantém os 44px da classe `alvo` — mínimo do HIG, medido nas
             decisões 207 e 208 —, e quem encolhe é o miolo. Encolher o botão
             devolveria um alvo de 32px, que é exatamente o defeito que aquelas
             duas varreduras corrigiram; aqui o dedo continua com a mesma área e
             só a tinta fica mais leve. */
          <button
            key={qual}
            type="button"
            aria-pressed={aba === qual}
            onClick={() => {
              trocarDeAba(qual)
            }}
            className="alvo"
          >
            <span
              className={`block rounded-full border px-3.5 py-1.5 text-[13px] ${
                aba === qual
                  ? 'border-transparent bg-[var(--text)] font-medium text-[var(--bg)]'
                  : 'border-[var(--border)] text-[var(--text-muted)]'
              }`}
            >
              {qual === 'produtos' ? 'Embalagens' : 'O que a casa compra'}
            </span>
          </button>
        ))}
      </nav>

      {aba === 'grupos' && <Familias versao={versaoDosGrupos} />}

      {/* Folha de baixo, e não um bloco que empurra a lista para longe
          (decisão 230). São oito campos: inline, eles jogavam o catálogo para
          fora da tela e a pessoa perdia de vista o que estava cadastrando ao
          lado do quê. A folha cobre, rola por dentro, e o Escape a fecha. */}
      {cadastrando && (
        <FolhaDeProduto
          produto={null}
          categorias={categorias}
          onFechar={() => {
            setCadastrando(false)
          }}
          onGravado={() => {
            // As duas listas: a de embalagens (estado desta tela) e a de
            // grupos (leitura própria) — senão o produto novo só aparecia
            // depois de trocar de aba.
            recarregar()
            setVersaoDosGrupos((v) => v + 1)
          }}
          onCategoriasMudaram={recarregar}
        />
      )}

      {aba === 'produtos' && (
        <Glass level="frost" className="p-5">
          <label className="mb-3 block">
            <span className="sr-only">Buscar produto</span>
            <input
              type="search"
              data-atalho-busca
              value={busca}
              placeholder="Buscar produto"
              className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
              onChange={(e) => {
                setBusca(e.target.value)
              }}
            />
          </label>

          {/* Só aparece quando há o que filtrar: um filtro que não muda nada é
            um controle a mais para a pessoa entender à toa. */}
          {Array.isArray(lista) && lista.some((p) => p.isMonitored) && (
            <button
              type="button"
              aria-pressed={soMonitorados}
              onClick={() => {
                setSoMonitorados((atual) => !atual)
              }}
              className={`alvo mb-3 rounded-full border px-4 text-xs ${
                soMonitorados
                  ? 'border-transparent bg-[var(--text)] text-[var(--bg)]'
                  : 'border-[var(--border)]'
              }`}
            >
              Só monitorados
            </button>
          )}

          {lista === 'loading' && (
            <p role="status" className="text-sm text-[var(--text-muted)]">
              Carregando…
            </p>
          )}
          {lista === 'failed' && (
            <p role="alert" className="text-sm text-[var(--text-muted)]">
              Não foi possível carregar o catálogo agora.
            </p>
          )}
          {Array.isArray(lista) &&
            (visiveis.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">
                {lista.length === 0
                  ? 'O catálogo está vazio. Cadastre um produto para começar a comparar preço.'
                  : 'Nenhum produto com esse nome.'}
              </p>
            ) : (
              <ul aria-label="Produtos">
                {visiveis.map((product) => (
                  <Linha
                    key={product.id}
                    product={product}
                    categorias={categorias}
                    onCategoria={(categoryId) => {
                      mudarCategoria(product.id, categoryId)
                    }}
                    onCriouCategoria={() => {
                      void categoryRepository.list().then(setCategorias)
                    }}
                  />
                ))}
              </ul>
            ))}
        </Glass>
      )}
    </section>
  )
}
